import {
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { objectEntriesTyped } from '@snap-types/util/object-iterators';
import { Button } from '@ui/components/Button';
import { snapSiteConfigSelectors } from '@ui/features/snap-site-config/state';
import { URLPaginator, usePaginationURLState } from '@ui/hooks/pagination';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';
import { QuoteSearchParams } from '@ui/pages/QuoteQueue/screen';

import { actions } from '../../state/reducer';

export const PART_SEARCH_COLUMNS_CUSTOM_NAMES = [
  'user1',
  'user2',
  'user3',
  'user4',
  'user5',
  'user6',
  'user7',
  'user8',
  'user9',
  'user10',
] as const;

export const PART_SEARCH_COLUMNS_FIXED_NAMES = {
  inventory: 'Inventory',
  customerPartId: 'Customer Part ID',
  commodityCode: 'Commodity Code',
  productCode: 'Product Code',
} as const;

type ChoosableFixedNames = keyof typeof PART_SEARCH_COLUMNS_FIXED_NAMES;

export const PART_SEARCH_CHOOSABLE_NAMES = [
  ...PART_SEARCH_COLUMNS_CUSTOM_NAMES,
  ...Object.keys(PART_SEARCH_COLUMNS_FIXED_NAMES),
];

export type PartSearchColumnChoice =
  | ChoosableFixedNames
  | (typeof PART_SEARCH_COLUMNS_CUSTOM_NAMES)[number];

function removeFilters(
  paginator: URLPaginator,
  columns: PartSearchColumnChoice[],
) {
  paginator.setSearchParams(
    (params: QuoteSearchParams) => {
      columns.forEach((column) => params.delete(column));

      return params;
    },
    { resetPagination: true },
  );
}

export function PartSearchColumnChooser() {
  const dispatch = useDispatch();

  const snapSiteConfig = useSelector((state) =>
    snapSiteConfigSelectors.selectSnapSiteConfig(state),
  );

  const selectedColumns = useSelector(
    (state) => state?.quoteBuilder?.partSearchColumns ?? [],
  );

  const [_, paginator] = usePaginationURLState();

  const handleChange = (selected: string | string[]) => {
    const newSelectedColumns = (
      Array.isArray(selected) ? selected : [selected]
    ) as PartSearchColumnChoice[];

    const removedColumns = selectedColumns.filter(
      (col) => !newSelectedColumns.includes(col),
    );

    removeFilters(paginator, removedColumns);

    dispatch(actions.partSearchColumnsUpdated(newSelectedColumns));
  };

  return (
    <Menu closeOnSelect={false}>
      <MenuButton as={Button} colorScheme="blue" width="250px">
        Column Chooser
      </MenuButton>

      <MenuList minWidth="240px">
        <MenuOptionGroup
          type="checkbox"
          value={selectedColumns}
          onChange={handleChange}
        >
          {objectEntriesTyped(PART_SEARCH_COLUMNS_FIXED_NAMES).map(
            ([field, name]) => {
              const enabled =
                snapSiteConfig?.[`partSearchField_${field}_enabled`];

              if (!enabled) {
                return;
              }

              return (
                <MenuItemOption key={field} value={field}>
                  {name}
                </MenuItemOption>
              );
            },
          )}

          {PART_SEARCH_COLUMNS_CUSTOM_NAMES.map((field) => {
            const enabled =
              snapSiteConfig?.[`partSearchField_${field}_enabled`];

            if (!enabled) {
              return;
            }
            const name = snapSiteConfig?.[`partSearchField_${field}_name`];

            return (
              <MenuItemOption key={field} value={field}>
                {name}
              </MenuItemOption>
            );
          })}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
}
